import { Fragment } from "react";
import { MapPin, Calendar, User, FileText, AlertTriangle, Mail, Phone } from "lucide-react";

const ABS_LOGO_URL = "https://customer-assets.emergentagent.com/job_code-ingest-4/artifacts/xytjzgbq_ABS%20logo_text_charcoal_500%20px.png";

function fmt(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

function calcScope(scope) {
  const totalModPct = (scope.modifiers || []).reduce((s, m) => s + (parseFloat(m.percentage) || 0), 0);
  let subtotal = 0;
  const items = (scope.line_items || []).map(item => {
    const qty = parseFloat(item.qty) || 0;
    const base = parseFloat(item.base_price) || 0;
    const modifiedPrice = base * (1 + totalModPct);
    const total = item.unit?.toLowerCase() === "unit" ? 0 : qty * modifiedPrice;
    subtotal += total;
    return { ...item, _modified_price: modifiedPrice, _total: total };
  });
  return { items: items.filter(i => i.qty > 0 || i._total > 0), subtotal };
}

function groupItems(items) {
  const hasGroups = items.some(i => i.group && String(i.group).trim());
  if (!hasGroups) return null;
  const groups = [];
  const map = new Map();
  items.forEach(item => {
    const g = (item.group || "").trim() || "Other";
    if (!map.has(g)) {
      const entry = { name: g, items: [], subtotal: 0 };
      map.set(g, entry);
      groups.push(entry);
    }
    const entry = map.get(g);
    entry.items.push(item);
    entry.subtotal += item._total;
  });
  return groups;
}

/**
 * Self-contained, print-friendly summary view used by both the dedicated
 * Summary page and the popout Summary modal inside Budget Builder.
 */
export default function BudgetSummaryContent({ budget, defaultQualification = "", showPrices = false }) {
  const info = budget.project_info || {};
  const scopes = budget.selected_scopes || [];
  const grandTotal = scopes.reduce((s, sc) => s + calcScope(sc).subtotal, 0);
  const disclaimer = (defaultQualification || "").trim();

  return (
    <div className="max-w-4xl mx-auto p-8" data-testid="budget-summary-content">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }} data-testid="summary-project-name">
              {info.project_name ? info.project_name.toUpperCase() : "UNTITLED BUDGET"} (BUDGET ONLY)
            </h1>
            {(info.architect || info.contact) && (
              <div className="flex items-center gap-1 text-sm text-slate-500">
                <User className="w-3.5 h-3.5" /> {info.architect || info.contact}
              </div>
            )}
          </div>
          <div>
            <img src={ABS_LOGO_URL} alt="ABS Inc." className="h-10 w-auto" />
          </div>
        </div>

        {disclaimer && (
          <div
            className="bg-red-50 border-l-4 border-red-700 rounded-sm p-3 mb-4 flex items-start gap-2"
            data-testid="summary-global-disclaimer"
            style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
          >
            <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 font-medium leading-relaxed">{disclaimer}</p>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          {info.plans_dated && (
            <div>
              <div className="text-xs text-slate-400 mb-0.5">Plans Dated</div>
              <div className="text-slate-700">{info.plans_dated} {(info.bid_phase || info.plan_version) && `(${info.bid_phase || info.plan_version})`}</div>
            </div>
          )}
          {(info.start_date || info.completion_date) && (
            <div>
              <div className="text-xs text-slate-400 mb-0.5">Schedule</div>
              <div className="text-slate-700">
                {info.start_date || "—"} to {info.completion_date || "—"}
              </div>
            </div>
          )}
          {info.schedule && !(info.start_date || info.completion_date) && (
            <div>
              <div className="text-xs text-slate-400 mb-0.5">Project Schedule</div>
              <div className="text-slate-700">{info.schedule}</div>
            </div>
          )}
          {info.site_location && (
            <div>
              <div className="text-xs text-slate-400 mb-0.5">Site Location</div>
              <div className="text-slate-700 flex items-center gap-1"><MapPin className="w-3 h-3" />{info.site_location}</div>
            </div>
          )}
          <div>
            <div className="text-xs text-slate-400 mb-0.5">Generated</div>
            <div className="text-slate-700 flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(budget.updated_at)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-0.5">ABS Contact</div>
            {budget.abs_contact?.email ? (
              <>
                <div className="text-slate-700 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>{budget.abs_contact.name}</span>
                </div>
                <div className="text-slate-700 flex items-center gap-1 mt-0.5">
                  <Mail className="w-3 h-3" />
                  <a href={`mailto:${budget.abs_contact.email}`} className="hover:underline">{budget.abs_contact.email}</a>
                </div>
              </>
            ) : (
              <div className="text-slate-700 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                <a href="mailto:estimating@absinc.com" className="hover:underline">estimating@absinc.com</a>
              </div>
            )}
            <div className="text-slate-700 flex items-center gap-1 mt-0.5">
              <Phone className="w-3 h-3" />
              <a href="tel:3032932096" className="hover:underline">303.293.2096</a>
            </div>
          </div>
        </div>
      </div>

      {/* Summary table */}
      <div className="bg-white border border-slate-200 rounded-sm mb-6" data-testid="scope-summary-table">
        <div className="px-5 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-700">Summary</h2>
          <p className="text-xs text-slate-400 mt-0.5">See following page(s) for additional information.</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-5 py-2.5 text-xs text-slate-500 font-medium">Scope</th>
              <th className="text-right px-5 py-2.5 text-xs text-slate-500 font-medium">Proposed Value</th>
            </tr>
          </thead>
          <tbody>
            {scopes.map((sc, i) => {
              const { subtotal } = calcScope(sc);
              return (
                <tr key={sc.scope_id} className="border-b border-slate-100" data-testid={`summary-scope-row-${i}`}>
                  <td className="px-5 py-3 text-slate-700">{sc.scope_name}</td>
                  <td className="px-5 py-3 td-num font-medium text-slate-800">{subtotal === 0 ? "N/A (See Unit Pricing)" : fmt(subtotal)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-[#161616]">
              <td className="px-5 py-3 text-white font-semibold text-sm">Total</td>
              <td className="px-5 py-3 td-num font-semibold text-white text-sm" data-testid="grand-total-summary">{fmt(grandTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Scope details */}
      {scopes.map((sc) => {
        const { items, subtotal } = calcScope(sc);
        if (items.length === 0) return null;
        const groups = groupItems(items);
        const renderRow = (item, idx) => (
          <tr key={idx} className="border-b border-slate-100">
            <td className="px-5 py-2.5 text-slate-700">{item.description}</td>
            <td className="px-4 py-2.5 td-num text-slate-600">{item.qty}</td>
            <td className="px-4 py-2.5 text-slate-500 text-xs">{item.unit}</td>
            {showPrices && <td className="px-4 py-2.5 td-num text-slate-600">${item._modified_price.toFixed(2)}</td>}
            <td className="px-4 py-2.5 td-num font-medium text-slate-800">{item._total > 0 ? fmt(item._total) : "—"}</td>
          </tr>
        );
        return (
          <div key={sc.scope_id} className="bg-white border border-slate-200 rounded-sm mb-4" data-testid={`scope-detail-${sc.scope_id}`}>
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-800" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {sc.scope_name}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">{sc.scope_code}</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="text-left px-5 py-2 text-xs text-slate-500 font-medium">Item</th>
                  <th className="text-right px-4 py-2 text-xs text-slate-500 font-medium">Qty</th>
                  <th className="text-left px-4 py-2 text-xs text-slate-500 font-medium">Unit</th>
                  {showPrices && <th className="text-right px-4 py-2 text-xs text-slate-500 font-medium">Price</th>}
                  <th className="text-right px-4 py-2 text-xs text-slate-500 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {groups ? (
                  groups.map(grp => (
                    <Fragment key={grp.name}>
                      <tr className="bg-[#161616]/5 border-b border-[#161616]/20">
                        <td colSpan={showPrices ? 5 : 4} className="px-5 py-2 text-xs font-semibold text-[#161616]">
                          {grp.name}
                        </td>
                      </tr>
                      {grp.items.map((item, idx) => renderRow(item, `${grp.name}-${idx}`))}
                      <tr className="bg-slate-50/70 border-b border-slate-200">
                        <td colSpan={showPrices ? 4 : 3} className="px-5 py-2 text-right text-xs text-slate-500 font-medium">{grp.name} Subtotal</td>
                        <td className="px-4 py-2 td-num font-semibold text-slate-800 text-xs">{fmt(grp.subtotal)}</td>
                      </tr>
                    </Fragment>
                  ))
                ) : (
                  items.map((item, idx) => renderRow(item, idx))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={showPrices ? 3 : 2} className="px-5 py-2.5 text-xs text-slate-400"></td>
                  <td className="px-4 py-2.5 text-right text-xs font-medium text-slate-500">Subtotal</td>
                  <td className="px-4 py-2.5 td-num font-semibold text-slate-900">{fmt(subtotal)}</td>
                </tr>
              </tfoot>
            </table>
            {sc.qualification_text && !disclaimer && (
              <div className="px-5 py-2.5 border-t border-slate-100">
                <p className="text-xs text-slate-400 italic">{sc.qualification_text}</p>
              </div>
            )}
          </div>
        );
      })}

      {/* Notes */}
      {info.notes && (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">Notes</span>
          </div>
          <p className="text-sm text-slate-600">{info.notes}</p>
        </div>
      )}
    </div>
  );
}
